import { useEffect, useRef, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook for WebSocket connection to receive real-time order updates.
 * 
 * @param {Function} onOrderUpdate - Callback function called when an order is updated
 * @param {Function} onOrderCreated - Callback function called when a new order is created
 * @param {boolean} enabled - Whether the WebSocket connection should be active (default: false; push отключён по умолчанию)
 * @returns {Object} Object containing connection status and manual reconnect function
 */
export const useOrdersWebSocket = (onOrderUpdate, onOrderCreated, enabled = false) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isManuallyClosedRef = useRef(false);
  const orderCreatedDebounceTimerRef = useRef(null);
  
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_DELAY = 3000; // 3 seconds
  const HEARTBEAT_INTERVAL = 30000; // 30 seconds
  const ORDER_CREATED_DEBOUNCE_MS = 1200; // batch of order_created → single refresh
  
  const heartbeatIntervalRef = useRef(null);
  
  const connect = useCallback(() => {
    if (!enabled) {
      return;
    }
    
    // Don't connect if already connected or connecting
    if (wsRef.current && (wsRef.current.readyState === WebSocket.CONNECTING || wsRef.current.readyState === WebSocket.OPEN)) {
      return;
    }
    
    try {
      const token = api.getToken();
      if (!token) {
        console.warn('No authentication token found, cannot connect to orders WebSocket');
        return;
      }
      
      // Determine WebSocket URL (WebSocket endpoints are directly under /ws/, not /api/v1/)
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      const wsUrl = `${wsProtocol}//${wsHost}/ws/orders/?token=${encodeURIComponent(token)}`;
      
      console.log('Connecting to orders WebSocket:', wsUrl.replace(token, 'TOKEN'));
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('Orders WebSocket connected');
        reconnectAttemptsRef.current = 0;
        isManuallyClosedRef.current = false;
        
        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              type: 'ping',
              timestamp: Date.now()
            }));
          }
        }, HEARTBEAT_INTERVAL);
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'pong') {
            // Heartbeat response, ignore
            return;
          }
          
          if (message.type === 'order_updated' && message.data) {
            console.log('Received order_updated via WebSocket:', message.data);
            if (onOrderUpdate) {
              onOrderUpdate(message.data);
            }
          } else if (message.type === 'order_created' && message.data) {
            // Debounce: many order_created in short time (e.g. returns import) → single callback
            if (orderCreatedDebounceTimerRef.current) {
              clearTimeout(orderCreatedDebounceTimerRef.current);
            }
            orderCreatedDebounceTimerRef.current = setTimeout(() => {
              orderCreatedDebounceTimerRef.current = null;
              if (onOrderCreated) {
                onOrderCreated(message.data);
              }
            }, ORDER_CREATED_DEBOUNCE_MS);
          } else if (message.type === 'returns_import_finished') {
            // Single event after bulk returns import → one refresh
            if (onOrderCreated) {
              onOrderCreated(null);
            }
          } else {
            console.log('Received unknown WebSocket message:', message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('Orders WebSocket error:', error);
      };
      
      ws.onclose = (event) => {
        console.log('Orders WebSocket closed', event.code, event.reason);
        
        // Clear heartbeat interval
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        // Attempt to reconnect if not manually closed and we haven't exceeded max attempts
        if (!isManuallyClosedRef.current && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          const delay = RECONNECT_DELAY * reconnectAttemptsRef.current; // Exponential backoff
          
          console.log(`Attempting to reconnect to orders WebSocket in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.warn('Max reconnection attempts reached for orders WebSocket');
        }
      };
      
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
  }, [enabled, onOrderUpdate, onOrderCreated]);
  
  const disconnect = useCallback(() => {
    isManuallyClosedRef.current = true;
    
    if (orderCreatedDebounceTimerRef.current) {
      clearTimeout(orderCreatedDebounceTimerRef.current);
      orderCreatedDebounceTimerRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);
  
  const reconnect = useCallback(() => {
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [disconnect, connect]);
  
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }
    
    // Cleanup on unmount or when disabled
    return () => {
      disconnect();
    };
  }, [enabled, connect, disconnect]);
  
  // Get connection status
  const isConnected = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
  
  return {
    isConnected,
    reconnect,
    disconnect
  };
};

